import axios from "../../api/axios";
import { toast } from 'react-toastify';

/**
 * API endpoints for Statement of Account operations
 */
export const ENDPOINTS = {
  GET_PROPERTY_ID: "/property/:id",
  GET_SOA: "v2/statement-of-account/",
  GET_SOA_DETAILS: "v2/statement-of-account/:id",
  GET_SOA_DETAILS_BY_TOKEN: "v2/statement-of-account/details/:id/:token?",
  CREATE_SOA: "v2/statement-of-account/",
  UPDATE_SOA: "v2/statement-of-account/:id",
  APPROVE_SOA: "v2/statement-of-account/approve/:id/:token",
  REJECT_SOA: "v2/statement-of-account/reject/:id/:token",
  APPROVE_SOA_JWT: "v2/statement-of-account/approve-jwt/:id",
  REJECT_SOA_JWT: "v2/statement-of-account/reject-jwt/:id",
  AUDITOR_APPROVE_SOA: "v2/statement-of-account/auditor-approve/:id",
  AUDITOR_REJECT_SOA: "v2/statement-of-account/auditor-reject/:id",
  SIGN_SOA: "v2/statement-of-account/sign/:id/:token",
  DELETE_SOA: "v2/statement-of-account/:id",
  GET_USER_DETAILS: "v2/statement-of-account/users/:id/details/:role",
  GET_SIGNED_URL: "v2/statement-of-account/get-signed-url",
  UPLOAD_DOCUMENT: "v2/statement-of-account/upload-documents/:id",
  RESEND_APPROVAL_LINKS: "v2/statement-of-account/resend-approval-links/:id",
  SEND_APPROVAL_REQUESTS: "v2/statement-of-account/send-approval-requests/:id",
  SEND_AND_SAVE_EMAIL: "v2/statement-of-account/send-email/:id",
  // Invoice module: the approved invoices a deduction may be linked to.
  ELIGIBLE_INVOICES: "v2/invoices/invoice/eligible-for-soa",
};

/**
 * Approved invoices for this SOA's property / building / unit that are not yet
 * used on another statement.
 * @param {{ propertyId: string, buildingName?: string, unitNo: string, soaId?: string }} params
 * @returns {Promise<Array>} Trimmed invoice rows
 */
export const getEligibleInvoicesForSOA = async (params) => {
  try {
    const response = await axios.get(ENDPOINTS.ELIGIBLE_INVOICES, { params });
    return response.data?.data || [];
  } catch (error) {
    handleApiError(error, "Failed to load eligible invoices");
  }
};

/**
 * Centralized error handler for API calls
 * @param {Error} error - Axios error object
 * @param {string} defaultMessage - Default error message
 */
const handleApiError = (error, defaultMessage) => {
  const message = error.response?.data?.message || error.message || defaultMessage;
  console.error(defaultMessage, error);
  toast.error(message);
  throw new Error(message);
};

/**
 * Fetch details of a property by ID
 * @param {string} id - Property ID
 * @returns {Promise} Property details
 */
export const getPropertyById = async (id) => {
  try {
    const url = ENDPOINTS.GET_PROPERTY_ID.replace(':id', id);
    const response = await axios.get(url);
    return response.data.property;
  } catch (error) {
    handleApiError(error, "Failed to fetch property details");
  }
};

/**
 * Fetch all Statements of Account
 * @param {Object} params - Query parameters (e.g., propertyId, buildingName)
 * @returns {Promise} List of SOAs
 */
export const getSOAList = async (params = {}) => {
  try {
    const response = await axios.get(ENDPOINTS.GET_SOA, { params });
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to fetch SOA list");
  }
};

/**
 * Fetch details of a specific SOA by ID
 * @param {string} id - SOA ID
 * @returns {Promise} SOA details
 */
export const getSOADetails = async (id) => {
  try {
    const url = ENDPOINTS.GET_SOA_DETAILS.replace(':id', id);
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to fetch SOA details");
  }
};

/**
 * Fetch details of a specific SOA using ID and optional token
 * @param {string} id - SOA ID
 * @param {string} [token] - Approval or preview token (optional)
 * @returns {Promise} SOA details
 */
export const getSOADetailsByToken = async (id, token = '') => {
  try {
    const url = ENDPOINTS.GET_SOA_DETAILS_BY_TOKEN.replace(':id', id).replace(':token?', token);
    const config = token
      ? { headers: { Authorization: `Bearer ${token}` } }
      : {};
    const response = await axios.get(url, config);
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to fetch SOA details with token");
  }
};

/**
 * Create a new Statement of Account
 * @param {Object} payload - SOA data (tenant, address, property_details, etc.)
 * @returns {Promise} Created SOA
 */
export const createSOA = async (payload) => {
  try {
    const response = await axios.post(ENDPOINTS.CREATE_SOA, payload, {
      headers: { 'Content-Type': 'application/json' },
    });
    toast.success(response.data.message || 'SOA created successfully');
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to create SOA");
  }
};

/**
 * Update an existing Statement of Account
 * @param {string} id - SOA ID
 * @param {Object} payload - Updated SOA data
 * @returns {Promise} Updated SOA
 */
export const updateSOA = async (id, payload) => {
  try {
    const url = ENDPOINTS.UPDATE_SOA.replace(':id', id);
    const response = await axios.put(url, payload, {
      headers: { 'Content-Type': 'application/json' },
    });
    toast.success(response.data.message || 'SOA updated successfully');
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to update SOA");
  }
};

/**
 * Approve a Statement of Account
 * @param {string} id - SOA ID
 * @param {string} token - Approval token
 * @param {Object} payload - Approval data (signature, optional comments)
 * @returns {Promise} Approval confirmation
 */
export const approveSOA = async (id, token, payload) => {
  try {
    const url = ENDPOINTS.APPROVE_SOA.replace(':id', id).replace(':token', token);
    const response = await axios.post(url, payload, {
      headers: { 'Content-Type': 'application/json' },
    });
    toast.success(response.data.message || 'SOA approved successfully');
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to approve SOA");
  }
};

/**
 * Reject a Statement of Account
 * @param {string} id - SOA ID
 * @param {string} token - Approval token
 * @param {Object} payload - Rejection data (optional comments)
 * @returns {Promise} Rejection confirmation
 */
export const rejectSOA = async (id, token, payload) => {
  try {
    const url = ENDPOINTS.REJECT_SOA.replace(':id', id).replace(':token', token);
    const response = await axios.post(url, payload, {
      headers: { 'Content-Type': 'application/json' },
    });
    toast.success(response.data.message || 'SOA rejected successfully');
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to reject SOA");
  }
};

/**
 * Approve a Statement of Account using the logged-in user (Bearer JWT)
 * @param {string} id - SOA ID
 * @param {Object} payload - Approval data (signature, optional comments)
 * @returns {Promise} Approval confirmation
 */
export const approveSOAWithJwt = async (id, payload) => {
  try {
    const url = ENDPOINTS.APPROVE_SOA_JWT.replace(':id', id);
    const response = await axios.post(url, payload, {
      headers: { 'Content-Type': 'application/json' },
    });
    toast.success(response.data.message || 'SOA approved successfully');
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to approve SOA with JWT");
  }
};

/**
 * Reject a Statement of Account using the logged-in user (Bearer JWT)
 * @param {string} id - SOA ID
 * @param {Object} payload - Rejection data (optional comments)
 * @returns {Promise} Rejection confirmation
 */
export const rejectSOAWithJwt = async (id, payload) => {
  try {
    const url = ENDPOINTS.REJECT_SOA_JWT.replace(':id', id);
    const response = await axios.post(url, payload, {
      headers: { 'Content-Type': 'application/json' },
    });
    toast.success(response.data.message || 'SOA rejected successfully');
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to reject SOA with JWT");
  }
};

/**
 * Approve a Statement of Account by an auditor
 * @param {string} id - SOA ID
 * @param {Object} payload - Auditor approval data (signature, optional comments)
 * @returns {Promise} Approval confirmation
 */
export const auditorApproveSOA = async (id, payload) => {
  try {
    const url = ENDPOINTS.AUDITOR_APPROVE_SOA.replace(':id', id);
    const response = await axios.post(url, payload, {
      headers: { 'Content-Type': 'application/json' },
    });
    toast.success(response.data.message || 'SOA approved by auditor successfully');
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to approve SOA by auditor");
  }
};

/**
 * Reject a Statement of Account by an auditor
 * @param {string} id - SOA ID
 * @param {Object} payload - Auditor rejection data (optional comments)
 * @returns {Promise} Rejection confirmation
 */
export const auditorRejectSOA = async (id, payload) => {
  try {
    const url = ENDPOINTS.AUDITOR_REJECT_SOA.replace(':id', id);
    const response = await axios.post(url, payload, {
      headers: { 'Content-Type': 'application/json' },
    });
    toast.success(response.data.message || 'SOA rejected by auditor successfully');
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to reject SOA by auditor");
  }
};

/**
 * Sign a Statement of Account
 * @param {string} id - SOA ID
 * @param {string} token - Approval token
 * @param {Object} payload - Signature data
 * @returns {Promise} Signature confirmation
 */
export const signSOA = async (id, token, payload) => {
  try {
    const url = ENDPOINTS.SIGN_SOA.replace(':id', id).replace(':token', token);
    const response = await axios.post(url, payload, {
      headers: { 'Content-Type': 'application/json' },
    });
    toast.success(response.data.message || 'SOA signed successfully');
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to sign SOA");
  }
};

/**
 * Delete a Statement of Account
 * @param {string} id - SOA ID
 * @returns {Promise} Deletion confirmation
 */
export const deleteSOA = async (id) => {
  try {
    const url = ENDPOINTS.DELETE_SOA.replace(':id', id);
    const response = await axios.delete(url);
    toast.success(response.data.message || 'SOA deleted successfully');
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to delete SOA");
  }
};

/**
 * Fetch user details by ID and role
 * @param {string} id - User ID
 * @param {string} role - User role (company, manager, technical saff)
 * @returns {Promise} User details
 */
export const getUserDetails = async (id, role) => {
  try {
    const validRoles = ['company', 'manager', 'technical staff', 'rm vendor'];
    if (!validRoles.includes(role)) {
      throw new Error('Invalid role specified');
    }

    const url = ENDPOINTS.GET_USER_DETAILS.replace(':id', id).replace(':role', role);
    const response = await axios.get(url);

    if (!response.data.success) {
      throw new Error(response.data.message);
    }

    return response.data.data;
  } catch (error) {
    handleApiError(error, "Failed to fetch user details");
  }
};

/**
 * Get signed URL for accessing or uploading SOA documents
 * @param {string} key - S3 object key
 * @param {string} [operation='get'] - Operation type ('get' or 'put')
 * @param {string} [token] - Optional approval token
 * @param {string} [soaId] - Optional SOA ID (required for approval token validation)
 * @returns {Promise} Signed URL and key
 */
export const getSignedUrl = async (key, operation = 'get', token, soaId) => {
  try {
    const params = { key, operation };

    if (token) {
      params.token = token;
    }

    if (soaId) {
      params.soaId = soaId;
    }

    const response = await axios.get(ENDPOINTS.GET_SIGNED_URL, { params });
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to get signed URL");
  }
};

/**
 * Upload documents for an SOA
 * @param {string} id - SOA ID
 * @param {File[]|File} files - Single file or array of files to upload
 * @param {Function} onUploadProgress - Progress callback
 * @returns {Promise} Uploaded document details
 */
export const uploadDocuments = async (id, files, onUploadProgress) => {
  try {
    const formData = new FormData();
    const fileArray = Array.isArray(files) ? files : [files];
    fileArray.forEach((file) => {
      if (file instanceof File) {
        formData.append('documents', file);
      }
    });

    const url = ENDPOINTS.UPLOAD_DOCUMENT.replace(':id', id);
    const response = await axios.post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    });
    toast.success(response.data.message || 'Documents uploaded successfully');
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to upload documents');
  }
};

/**
 * Resend approval links to non-approved approvers for an SOA
 * @param {string} id - SOA ID
 * @returns {Promise} Confirmation of resent approval links
 */
export const resendApprovalLinks = async (id) => {
  try {
    const url = ENDPOINTS.RESEND_APPROVAL_LINKS.replace(':id', id);
    const response = await axios.post(url, {}, {
      headers: { 'Content-Type': 'application/json' },
    });
    toast.success(response.data.message || 'Approval links resent successfully');
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to resend approval links");
  }
};

/**
 * Send approval requests for an SOA
 * @param {string} id - SOA ID
 * @returns {Promise} Confirmation of sent approval requests
 */
export const sendApprovalRequests = async (id) => {
  try {
    const url = ENDPOINTS.SEND_APPROVAL_REQUESTS.replace(':id', id);
    const response = await axios.post(url, {}, {
      headers: { 'Content-Type': 'application/json' },
    });
    toast.success(response.data.message || 'Approval requests sent successfully');
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to send approval requests");
  }
};

/**
 * Fetch company details by property ID
 * @param {string} propertyId - Property ID
 * @returns {Promise} Company details
 */
export const getCompanyByPropertyId = async (propertyId) => {
  try {
    const url = `/company/company-by-property/${propertyId}`;
    const response = await axios.get(url);
    return response.data.company;
  } catch (error) {
    handleApiError(error, "Failed to fetch company details");
  }
};

/**
 * Send and save tenant email for an SOA
 * @param {string} id - SOA ID
 * @param {Object} emailData - Email details (name, email, cc, attachments, notes, subject)
 * @returns {Promise} Response from server
 */
export const sendAndSaveEmail = async (id, emailData) => {
  try {
    const url = ENDPOINTS.SEND_AND_SAVE_EMAIL.replace(':id', id);
    const response = await axios.post(url, emailData, {
      headers: { 'Content-Type': 'application/json' },
    });

    toast.success(response.data.message || 'Email sent successfully');
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to send and save email');
  }
};
